import { useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import api, { formatError } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Building2, HeartPulse, ShieldCheck, FileCheck2, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import TermsCheckbox from "@/components/TermsCheckbox";

const MAX_KYC_BYTES = 5 * 1024 * 1024;
const KYC_ALLOWED = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];

export default function AdminLogin() {
  const { type } = useParams(); // hospital | clinic
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const role = type === "clinic" ? "clinic_admin" : "hospital_admin";
  const isClinic = type === "clinic";

  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [kycFile, setKycFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingMsg, setPendingMsg] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setKycFile(null);
    if (!KYC_ALLOWED.includes(f.type)) {
      toast.error("Only PDF, JPG, or PNG files are allowed");
      e.target.value = "";
      return setKycFile(null);
    }
    if (f.size > MAX_KYC_BYTES) {
      toast.error("File exceeds 5 MB limit");
      e.target.value = "";
      return setKycFile(null);
    }
    setKycFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (tab === "login") {
        const { data } = await api.post("/auth/admin/login", { email, password, role });
        setSession(data);
        toast.success("Welcome back");
        navigate("/admin/dashboard");
      } else {
        if (!licenseNumber.trim()) {
          toast.error("CMHO / Registration license number is required");
          setSubmitting(false);
          return;
        }
        if (!kycFile) {
          toast.error("Please upload your Registration Document");
          setSubmitting(false);
          return;
        }
        const fd = new FormData();
        fd.append("name", name);
        fd.append("email", email);
        fd.append("password", password);
        fd.append("org_name", orgName);
        fd.append("org_type", isClinic ? "clinic" : "hospital");
        fd.append("license_number", licenseNumber.trim());
        fd.append("kyc_document", kycFile);
        fd.append("accepted_terms", "true");
        const { data } = await api.post("/auth/admin/register", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPendingMsg(data.message || "Registration submitted for KYC review.");
        toast.success("Registration submitted");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = isClinic ? HeartPulse : Building2;
  const accent = isClinic ? "emerald" : "sky";
  const title = isClinic ? "Clinic" : "Hospital";

  if (pendingMsg) {
    return (
      <div className="min-h-screen bg-slate-50 bg-grain flex flex-col" data-testid="admin-kyc-pending-page">
        <header className="px-6 md:px-12 py-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-gateway">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </header>
        <main className="flex-1 flex items-center justify-center px-6 pb-16">
          <Card className="w-full max-w-lg p-8 medical-card">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-300 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-semibold text-slate-900">Registration Under Review</h1>
                <p className="text-sm text-slate-500 mt-1">KYC verification in progress</p>
              </div>
            </div>
            <p className="text-slate-700 leading-relaxed" data-testid="kyc-pending-message">
              {pendingMsg}
            </p>
            <ul className="mt-5 space-y-2 text-sm text-slate-600">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> CMHO license verified against registry</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> Registration document reviewed manually</li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> Notification on approval via WhatsApp + SMS</li>
            </ul>
            <div className="mt-6 flex gap-3">
              <Button onClick={() => navigate("/")} variant="outline" data-testid="kyc-done-btn">Back to Home</Button>
            </div>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 bg-grain flex flex-col" data-testid={`${type}-login-page`}>
      <header className="px-6 md:px-12 py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-gateway">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 pb-16">
        <Card className="w-full max-w-md p-8 medical-card">
          <div className="flex items-center gap-3 mb-6">
            <div className={`w-11 h-11 rounded-xl bg-${accent}-500 text-white flex items-center justify-center`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900">{title} Portal</h1>
              <p className="text-xs text-slate-500">Sign in or create an admin account</p>
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <TabsContent value="register" className="space-y-4 mt-0">
                <div>
                  <Label htmlFor="name">Your Name</Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required={tab === "register"} data-testid="admin-name-input" placeholder="Dr. Admin Name" />
                </div>
                <div>
                  <Label htmlFor="orgName">{title} Name</Label>
                  <Input id="orgName" value={orgName} onChange={(e) => setOrgName(e.target.value)} required={tab === "register"} data-testid="admin-orgname-input" placeholder={`${title} ABC`} />
                </div>
                <div>
                  <Label htmlFor="license">CMHO / Registration License No.<span className="text-rose-500"> *</span></Label>
                  <Input id="license" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} required={tab === "register"} data-testid="admin-license-input" placeholder="e.g. CMHO/2024/1234" />
                  <p className="text-[11px] text-slate-500 mt-1">Enter your Chief Medical &amp; Health Officer license number.</p>
                </div>
                <div>
                  <Label htmlFor="kyc-doc">Registration Document (PDF/JPG/PNG, ≤5MB)<span className="text-rose-500"> *</span></Label>
                  <div className="mt-1 flex items-center gap-3">
                    <label className="flex-1 cursor-pointer" data-testid="admin-kyc-file-picker">
                      <input id="kyc-doc" type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={onFile} className="hidden" data-testid="admin-kyc-file-input" />
                      <div className={`border-2 border-dashed rounded-lg px-3 py-3 text-sm flex items-center gap-2 ${kycFile ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-slate-300 hover:border-slate-400 text-slate-600"}`}>
                        <FileCheck2 className="w-4 h-4 shrink-0" />
                        <span className="truncate">{kycFile ? `${kycFile.name} (${(kycFile.size/1024).toFixed(0)} KB)` : "Choose registration document"}</span>
                      </div>
                    </label>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Your document is stored privately and reviewed only by the SuperAdmin team.</p>
                </div>
              </TabsContent>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email-input" placeholder="admin@hospital.com" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="admin-password-input" placeholder="Min 6 characters" />
              </div>

              {tab === "register" && (
                <TermsCheckbox
                  checked={acceptedTerms}
                  onChange={setAcceptedTerms}
                  testid="admin-terms-checkbox"
                />
              )}

              <Button type="submit" disabled={submitting || (tab === "register" && !acceptedTerms)} className={`w-full bg-${accent}-500 hover:bg-${accent}-600 text-white`} data-testid="admin-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : tab === "login" ? "Sign In" : "Submit for KYC Review"}
              </Button>
            </form>
          </Tabs>
        </Card>
      </main>
    </div>
  );
}
